import type { AuthAccountWithPassType } from 'api-client/accounts';
import { login } from 'api-client/accounts';
import { LoginProviderEnum } from 'bc-model';
import { ODSAlert, ODSButton, ODSFormGroup, ODSLabel } from 'bc-ods';
import ContactUsModal from 'components/Brief/ArtifactNavbar/ContactUsModal';
import { isThirdPartyProvider } from 'components/Login/RegistrationForm';
import ValidatedFormikField from 'components/Utils/Forms/ValidatedFormikField';
import { Form, Formik } from 'formik';
import useToggle from 'hooks/useToggle';
import moment from 'moment';
import { parse } from 'qs';
import type { ReactElement } from 'react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { clearUserData } from 'utils/clearUserData';
import yup from 'utils/yup';
import {
  FormStepEnum,
  SmartLoginActionsContext,
  SmartLoginStateContext,
} from './context/SmartLoginContextProvider';

const messages = defineMessages({
  button: {
    id: 'SmartLoginForm.button',
    defaultMessage:
      '{step, select,' +
      '  Login {Signing in\u2026}' +
      '  AuthenticateAndLogin {Sign in}' +
      '  other {Continue}' +
      '}',
  },
  forgot: {
    id: 'SmartLoginForm.forgot',
    defaultMessage: 'Forgot password',
  },
  help: {
    id: 'SmartLoginForm.help',
    defaultMessage: 'Help',
  },
  passwordLabel: {
    id: 'SmartLoginForm.passwordLabel',
    defaultMessage: 'Password',
  },
  usernameLabel: {
    id: 'SmartLoginForm.usernameLabel',
    defaultMessage: '{onlyEmail, select, true {Email} other {Email or username}}',
  },
  usernamePlaceholder: {
    id: 'SmartLoginForm.usernamePlaceholder',
    defaultMessage: 'e.g. dave@navy.mil',
  },
  usernameFeedback: {
    id: 'SmartLoginForm.usernameFeedback',
    defaultMessage:
      '{error, select, min {Must be at least 3 characters long} noSpaces {No spaces allowed} other {Required}}',
  },
  passwordFeedback: {
    id: 'SmartLoginForm.passwordFeedback',
    defaultMessage: '{error, select,  min {Must be longer than 6 characters} other {Required}}',
  },
  unknownError: {
    id: 'SmartLoginForm.unknownError',
    defaultMessage: 'An unknown error occurred while processing your request. Please try again.',
  },
  buttonBack: {
    id: 'SmartLoginForm.buttonBack',
    defaultMessage: 'Back',
  },
  logoutReason: {
    id: 'SmartLoginForm.logoutReason',
    // PSA: Unfortunately messages MUST be string literals or else they don't extract properly,
    //  so do not use string interpolation for the enum values
    defaultMessage: `{reason, select,
        CLOSED {Session ended due to login in another location.}
        EXPIRED {Session expired due to inactivity.}
        SIGNED_OUT {You have been signed out.}
        UNKNOWN {You have been signed out.}
        DEACTIVATED {Your account has been deactivated.}
        CANCELED_PW_RESET {You have been signed out.}
        other {You have been signed out.}
      }`,
  },
  newUserPrompt: {
    id: 'SmartLoginForm.newUserPrompt',
    defaultMessage: 'Sign up',
  },
  enhancedNewUserPrompt: {
    id: 'SmartLoginForm.enhancedNewUserPrompt',
    defaultMessage: 'Click here to create an account',
  },
  cuiDisclaimer: {
    id: 'SmartLoginForm.cuiDisclaimer',
    defaultMessage: 'CUI data is not authorized in this instance of Onebrief',
  },
});

const INITIAL_VALUES = { username: '', password: '' };

// We are not using react-intl for this right now because the server returns an untranslated
// error string. Translating only the remaining lockout time message could mix languages in a
// single message. If/when the API is updated to return error codes instead of strings, we'll
// migrate this to react-intl as well.
function getRemainingLockoutTime(seconds?: number | null): string {
  if (!seconds || !Number.isFinite(seconds) || seconds < 0) return '';
  const timer = moment.utc(seconds * 1000).format('mm:ss');
  const unit = seconds >= 60 ? 'minutes' : 'seconds';
  return `${timer} ${unit} remaining`;
}

function useHandleSubmit(setShowError, intl) {
  const { queryString, accountLinkingEnabled } = useContext(SmartLoginStateContext);
  const {
    setUserProfileInfo,
    setFormError,
    setFormStep,
    setRedirectLocationAfterLogin,
    setLoginProvider,
    setMatchingId,
  } = useContext(SmartLoginActionsContext);

  const authenticate = useCallback(
    async (values) => {
      try {
        const result = await login(values.username, values.password, queryString);
        if (isThirdPartyProvider(result.data.loginProvider) && result.data.userDetails) {
          const { ...profileDetails } = result.data.userDetails;
          const profile: AuthAccountWithPassType = profileDetails;
          setUserProfileInfo(profile);
          setLoginProvider(result.data.loginProvider);
        }

        if (result.data.redirect) setRedirectLocationAfterLogin(result.data.redirect);

        let currentFormStep = FormStepEnum.Login;

        if (result.data.hasUnlinkedProfile) {
          currentFormStep = accountLinkingEnabled
            ? FormStepEnum.SSOAccountDiscovered
            : FormStepEnum.Register;
        } else if (result.data.mustRegister) {
          currentFormStep = accountLinkingEnabled
            ? FormStepEnum.SSOChooseLinkOrRegister
            : FormStepEnum.Register;
        }

        if (result.data.hasUnlinkedProfile) {
          if (result.data.userDetails.email) {
            setMatchingId(result.data.userDetails.email);
          } else if (result.data.userDetails.username) {
            setMatchingId(result.data.userDetails.username);
          }
        }

        setFormStep(currentFormStep);
      } catch (e: any) {
        const data = e?.response?.data ?? {};
        const { error, lockout } = data;
        const secondsRemaining = lockout?.secondsRemaining;

        let formError = error ?? intl.formatMessage(messages.unknownError);

        if (lockout?.locked && typeof secondsRemaining === 'number' && secondsRemaining > 0) {
          const remainingLockoutTime = getRemainingLockoutTime(secondsRemaining);
          if (remainingLockoutTime) {
            formError += ` ${remainingLockoutTime}.`;
          }
        }

        setFormError(formError);
        setShowError(true);
      }
    },
    [
      setUserProfileInfo,
      setFormStep,
      setFormError,
      setMatchingId,
      setShowError,
      setRedirectLocationAfterLogin,
      intl,
      queryString,
      setLoginProvider,
      accountLinkingEnabled,
    ]
  );

  const handleSubmit = useCallback(
    async (values) => {
      setShowError(false);
      await authenticate(values);
    },
    [authenticate, setShowError]
  );

  return handleSubmit;
}

function SignInForm(): ReactElement {
  const intl = useIntl();
  const [showError, setShowError] = useState(false);
  const { formStep, formError, allowedSchemas, showCuiDisclaimer, profile } =
    useContext(SmartLoginStateContext);
  const { setFormStep, setName } = useContext(SmartLoginActionsContext);
  const { search: queryString } = useLocation();
  const { reason } = parse(queryString, { ignoreQueryPrefix: true });

  // TODO(DTP-72) Yes, this is a dumb place for this. It's also Saturday morning.
  // We can engineer it better during business hours.
  useEffect(() => {
    void clearUserData();
  }, []);

  useEffect(() => {
    if ((profile as any).autoRegister) {
      // SmartLoginContextProvider is setting the profile after the form is rendered, so we need to wait a bit.
      // This is a silly workaround, but this code is only meant to be used by devs so it's fine if it breaks,
      // and it's not worth the risk of changing the SmartLoginContextProvider logic as that's been working fine.
      setTimeout(() => {
        setFormStep(FormStepEnum.Register);
      }, 100);
    }
  }, [profile, setFormStep]);

  const signInFieldValidationSchema = yup.object().shape({
    username: yup.string().required('required').min(3, 'min').matches(/^\S*$/, 'noSpaces'),
    ...(formStep === FormStepEnum.AuthenticateAndLogin && {
      password: yup.string().required('required').min(6, 'min'),
    }),
  });

  const forgotPassword = useCallback(
    (name: string) => {
      setShowError(false);
      setName(name);
      setFormStep(FormStepEnum.Forgot);
    },
    [setShowError, setName, setFormStep]
  );

  const localAuthEnabled = useMemo(() => {
    return allowedSchemas.includes(LoginProviderEnum.Local);
  }, [allowedSchemas]);

  const handleSubmit = useHandleSubmit(setShowError, intl);
  const [contactUsModalVisible, toggleContactUsModal] = useToggle();

  return (
    <>
      <Formik
        initialValues={INITIAL_VALUES}
        onSubmit={handleSubmit}
        className="w-100"
        validationSchema={signInFieldValidationSchema}
        validateOnChange={false}
      >
        {({ errors, values, isSubmitting }) => (
          <div>
            {reason && (
              <div className={'mb-3'}>
                <ODSAlert color="info" isOpen={!!reason}>
                  <FormattedMessage
                    {...messages.logoutReason}
                    values={{ reason: reason as React.ReactNode }}
                  />
                </ODSAlert>
              </div>
            )}
            {showError && (
              <div className="mb-3">
                <ODSAlert
                  data-testid="sign-in-form-error"
                  color="danger"
                  isOpen={showError}
                  className="d-flex align-items-center"
                >
                  {formError}
                </ODSAlert>
              </div>
            )}
            <Form data-testid="sign-in-form">
              <ODSFormGroup>
                <ValidatedFormikField
                  name="username"
                  id="username"
                  label={intl.formatMessage(messages.usernameLabel, {
                    onlyEmail: allowedSchemas.includes(LoginProviderEnum.ActiveDirectory),
                  })}
                  size="md"
                  placeholder={intl.formatMessage(messages.usernamePlaceholder)}
                  autoFocus
                  data-testid="username"
                  autoComplete="username"
                  tabIndex={1000}
                  errorMessage={intl.formatMessage(messages.usernameFeedback, {
                    error: errors.username,
                  })}
                />
              </ODSFormGroup>
              {[FormStepEnum.AuthenticateAndLogin, FormStepEnum.Login].includes(formStep) && (
                <ODSFormGroup>
                  {localAuthEnabled && (
                    <div className="d-flex justify-content-between align-items-baseline">
                      <ODSLabel htmlFor="password">
                        <FormattedMessage {...messages.passwordLabel} />
                      </ODSLabel>

                      <div className="d-flex">
                        <ODSButton
                          color="primary"
                          quiet
                          className="text-decoration-underline"
                          data-testid="forgot"
                          onClick={() => {
                            forgotPassword(values.username);
                          }}
                          size="sm"
                          tabIndex={1030}
                        >
                          <FormattedMessage {...messages.forgot} />
                        </ODSButton>

                        <div className="vr align-self-center" />

                        <ODSButton
                          color="primary"
                          quiet
                          className="text-decoration-underline"
                          data-testid="help"
                          onClick={toggleContactUsModal}
                          size="sm"
                          tabIndex={1030}
                        >
                          <FormattedMessage {...messages.help} />
                        </ODSButton>
                      </div>
                    </div>
                  )}
                  <ValidatedFormikField
                    name="password"
                    id="password"
                    type="password"
                    size="md"
                    data-testid="password"
                    autoComplete="current-password"
                    tabIndex={1010}
                    errorMessage={intl.formatMessage(messages.passwordFeedback, {
                      error: errors.password,
                    })}
                  />
                </ODSFormGroup>
              )}
              <ODSButton
                color="primary"
                size="md"
                className="w-100"
                disabled={isSubmitting ?? formStep === FormStepEnum.Login}
                type="submit"
                data-testid="submit"
                tabIndex={1020}
              >
                <FormattedMessage {...messages.button} values={{ step: formStep }} />
              </ODSButton>
              {localAuthEnabled && (
                <ODSButton
                  color="primary"
                  quiet
                  size="md"
                  className="w-100 text-decoration-underline mt-2"
                  data-testid="sign-up"
                  tabIndex={1040}
                  onClick={() => {
                    setFormStep(FormStepEnum.Register);
                  }}
                >
                  <FormattedMessage {...messages.enhancedNewUserPrompt} />
                </ODSButton>
              )}
            </Form>
            {showCuiDisclaimer && (
              <ODSAlert
                data-testid="cui-disclaimer"
                color="warning"
                isOpen={showCuiDisclaimer}
                className="mt-3 mb-0 text-center py-2 small"
              >
                <FormattedMessage {...messages.cuiDisclaimer} />
              </ODSAlert>
            )}
          </div>
        )}
      </Formik>

      <ContactUsModal isOpen={contactUsModalVisible} toggle={toggleContactUsModal} />
    </>
  );
}

const TEST_ACCESS = { getRemainingLockoutTime, messages };

export { TEST_ACCESS };
export default SignInForm;
